import type { CurrencyDTO } from 'store/accounts/dtos';
import type { UserStatus } from 'store/auth/types';
import type { MaintenanceWindowDTO } from 'store/common/dtos';
import type { PlanID, ServiceID } from 'store/orders/types';
import type { Nullable } from 'types';

import type {
  AuthenticationType,
  CredentialsGeneratorVersion,
  HostnameRegion,
  IPVersion,
  NetworkType,
  OperationStatus,
  Protocol,
  ProxyBulkOperationChanges,
  ProxySubnetStatus,
} from './types';

// #region Common
type ChangeableOption = {
  id: number;
  name: string;
};

export enum ProxyActionDTO {
  ACTIVATE = 'ACTIVATE',
  ADMIN_DETAILS = 'ADMIN_DETAILS',
  AUTO_EXTEND = 'AUTO_EXTEND',
  AUTO_EXTEND_TRAFFIC = 'AUTO_EXTEND_TRAFFIC',
  BUY_TRAFFIC = 'BUY_TRAFFIC',
  BUY_THREADS = 'BUY_THREADS',
  BUY_UPLINK_SPEED = 'BUY_UPLINK_SPEED',
  CANCEL = 'CANCEL',
  CHANGE_AUTHENTICATION_METHOD = 'CHANGE_AUTHENTICATION_METHOD',
  CHANGE_HTTPS_PROTOCOL = 'CHANGE_HTTPS_PROTOCOL',
  CHANGE_PORT = 'CHANGE_PORT',
  CHANGE_PROTOCOL = 'CHANGE_PROTOCOL',
  CHANGE_THREADS = 'CHANGE_THREADS',
  CHANGE_UPLINK_SPEED = 'CHANGE_UPLINK_SPEED',
  CHANGE_WHITELIST_IP = 'CHANGE_WHITELIST_IP',
  CREATE_ROUTE = 'CREATE_ROUTE',
  DELETE_ROUTE = 'DELETE_ROUTE',
  DOWNLOAD_CREDENTIALS = 'DOWNLOAD_CREDENTIALS',
  EXTEND_PERIOD = 'EXTEND_PERIOD',
  EXPIRE = 'EXPIRE',
  GENERATE_CREDENTIALS = 'GENERATE_CREDENTIALS',
  INITIALIZE = 'INITIALIZE',
  LIST_EVENTS = 'LIST_EVENTS',
  LIST_HISTORY_CHANGES = 'LIST_HISTORY_CHANGES',
  REACTIVATE = 'REACTIVATE',
  REGENERATE_CREDENTIALS = 'REGENERATE_CREDENTIALS',
  REFUND = 'REFUND',
  REVIVE = 'REVIVE',
  ROTATE_IP = 'ROTATE_IP',
  RUN_DIAGNOSTIC_ROUTINE = 'RUN_DIAGNOSTIC_ROUTINE',
  RUN_TEST = 'RUN_TEST',
  ORDERING_SUBNET = 'ORDERING_SUBNET',
  CURL_EXAMPLE = 'CURL_EXAMPLE',
  TRAFFIC_ACCOUNTING = 'TRAFFIC_ACCOUNTING',
  TRAFFIC_USAGE_OVERVIEW = 'TRAFFIC_USAGE_OVERVIEW',
  UPTIME_MONITORING = 'UPTIME_MONITORING',
  CONNECT_WITH_CHROME_EXTENSION = 'CONNECT_WITH_CHROME_EXTENSION',
  CONNECT_WITH_FIREFOX_EXTENSION = 'CONNECT_WITH_FIREFOX_EXTENSION',
  CONNECT_WITH_VPN_APP = 'CONNECT_WITH_VPN_APP',
  DISPLAY_MOBILE_ALERT = 'DISPLAY_MOBILE_ALERT',
  DISPLAY_RESIDENTIAL_ALERT = 'DISPLAY_RESIDENTIAL_ALERT',
}

export type ProxyAdminPricesDTO = {
  unitPrice: Nullable<string>;
  totalValue: Nullable<string>;
};

export type ProxyAdminServerDTO = {
  id: string;
  name: string;
  ip: string;
};

export type ProxyAdminSubnetDTO = {
  id: string;
  subnetCidr: string;
  status: ProxySubnetStatus;
};

export type ProxyAdminVendorDTO = {
  urlText: string;
  url: string;
};

export type ProxyAuthenticationDTO = {
  whitelistedIps: string[];
  username: Nullable<string>;
  password: Nullable<string>;
};

export type ProxyBandwidthDTO = {
  total: Nullable<number>;
  used: Nullable<number>;
  updatedAt: string;
};

export type ProxyConnectionDTO = {
  publicIp: Nullable<string>;
  connectIp: Nullable<string>;
  ipVersion: IPVersion;
  lastIp: Nullable<string>;
  httpPort: Nullable<number>;
  httpsPort: Nullable<number>;
  socks5Port: Nullable<number>;
  hostnames: [] | Record<string, string>;
};

export type ProxyLocationDTO = {
  countryCode: string;
  regionCode: Nullable<string>;
};

export type ProxyMetadataDTO = {
  ispName: Nullable<string>;
};

export type ProxyRouteDTO = {
  id: number;
  proxyService: number;
  server: string;
  publicIp: string;
  port: number;
  serverName: string;
};

export enum ProxyStatusDTO {
  PENDING = 'PENDING',
  INITIATING = 'INITIATING',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
}

export enum ProxyStatusChangeFailureReasonDTO {
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  ORDER_PENDING = 'ORDER_PENDING',
  INVALID_ORDER = 'INVALID_ORDER',
  NOT_ENOUGH_BALANCE = 'NOT_ENOUGH_BALANCE',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export type ProxyThreadsDTO = {
  value: number;
  label: string;
};

export type ProxyUplinkSpeedDTO = {
  value: number;
  label: string;
};

type UpgradeableOption = ChangeableOption & {
  unitPrice: string;
  totalPrice: string;
};

export type ProxyTrafficDataDTO = {
  date: string;
  value: number;
};

export type ProxyTrafficDTO = {
  updatedAt: string;
  data: ProxyTrafficDataDTO[];
};

export type ProxyUptimeDataDTO = {
  date: string;
  value: Nullable<number>;
};

export type ProxyUptimeDTO = {
  updatedAt: string;
  data: ProxyUptimeDataDTO[];
};

export enum LimitParameterDTO {
  BANDWIDTH = 'BANDWIDTH',
  SPEED = 'SPEED',
  CONCURRENCY = 'CONCURRENCY',
}

// Technical unit, not localized — passed through as-is, no DTO->Model conversion needed.
export type LimitUnitDTO = 'GB' | 'Mbit' | 'Gbit' | 'connections';

export type ProxyLimitHourlyDTO = {
  hour: string;
  peak: Nullable<number>;
};

export type ProxyLimitSignalDTO = {
  parameter: LimitParameterDTO;
  peak: number;
  limit: number;
  unit: LimitUnitDTO;
  hourly: Nullable<ProxyLimitHourlyDTO[]>;
  upgradeAvailable: boolean;
};

// #endregion Common

/**
 * @POST /proxies/bulk/auto-extend/details
 */
export type BulkAutoExtendSettingsDTO = {
  isBandwidthRequired: boolean;
  isAutoExtendEnabled: boolean;
};

/**
 * @GET /proxies/{id}/change-authentication/type
 */
export type ChangeAuthenticationTypeDTO = {
  currentAuthenticationType: AuthenticationType;
  availableAuthenticationTypes: AuthenticationType[];
};

/**
 * @GET /proxies/{id}/change-protocol
 */
export type ChangeProtocolDTO = {
  currentType: Protocol;
  availableTypes: Protocol[];
};

/**
 * @GET /proxies/{id}/change-threads
 */
export type ChangeThreadsDTO = {
  currentThreads: number;
  availableThreads: ChangeableOption[];
};

/**
 * @GET /proxies/{id}/change-uplink-speed
 */
export type ChangeUplinkSpeedDTO = {
  currentUplinkSpeed: number;
  availableUplinkSpeeds: ChangeableOption[];
};

/**
 * @POST /proxies/{id}/admin/diagnostic-routine
 */
export type DiagnosticRoutineDTO = {
  url: string;
  statusCode: number;
  statusMessage: string;
  time: number;
  metadata: Record<string, string>;
};

/**
 * @GET /proxies/settings/global-ip-whitelist
 * @GET /proxies/{id}/whitelist-ip
 */
export type IPWhitelistDTO = {
  whitelistedIps: string[];
  allowedWhitelistIpCount: number;
};

/**
 * @GET /proxies/{id}/credential-generator
 */

type CredentialsDTO = {
  hostnameIp: string;
  networkType: NetworkType;
  version: CredentialsGeneratorVersion;
  region: HostnameRegion;
};

type LocationsDTO = Record<string, Nullable<Record<string, Nullable<string[]>>>>;

export type CredentialsGeneratorDTO = {
  credentials: CredentialsDTO[];
  locations: LocationsDTO;
};

/**
 * @GET /services/proxies
 */
export type ProxyDTO = {
  id: number;
  status: ProxyStatusDTO;
  networkType: NetworkType;
  authentication: ProxyAuthenticationDTO;
  connection: ProxyConnectionDTO;
  proxyType: Protocol;
  createdAt: string;
  expiresAt: Nullable<string>;
  /**
   * Stored service identifier of the proxy's purchase order, e.g. `static-residential-ipv4`.
   * `null` where the order has no service recorded.
   */
  serviceId: Nullable<ServiceID>;
  /**
   * Stored plan of the proxy's purchase order. `null` where the order has no plan recorded (older orders).
   */
  planId: Nullable<PlanID>;
  /**
   * This proxy's share of its purchase order's total price, truncated to whole cents.
   * `null` where the proxy has no purchase order.
   *
   * Intended for retention/renewal-rate analytics, not as a revenue figure.
   */
  price: Nullable<number>;
  metadata: ProxyMetadataDTO;
  bandwidth: ProxyBandwidthDTO;
  uplinkSpeed: Nullable<ProxyUplinkSpeedDTO>;
  threads: Nullable<ProxyThreadsDTO>;
  location: ProxyLocationDTO;
  note: Nullable<string>;
  actions: ProxyActionDTO[];
  maintenanceWindows: MaintenanceWindowDTO[];
};

/**
 * @GET /proxies/{id}/admin/details
 */
export type ProxyAdminDetailsDTO = {
  userName: string;
  userId: number;
  status: UserStatus;
  proposition: Nullable<string>;
  reservedIp: Nullable<string>;
  server: Nullable<ProxyAdminServerDTO>;
  subnet: Nullable<ProxyAdminSubnetDTO>;
  isUsed: boolean;
  isHttpsProtocolSupported: boolean;
  vendor: ProxyAdminVendorDTO[];
  prices: Nullable<ProxyAdminPricesDTO>;
  routes: ProxyRouteDTO[];
  type: 'basic' | 'standard' | 'premium';
  statusChangeFailureReason: Nullable<ProxyStatusChangeFailureReasonDTO>;
};

/**
 * @GET /proxies/{id}/change-history
 */
export type ProxyChangelogDTO = {
  oldValue: string;
  newValue: string;
  createdAt: string;
  fieldName: string;
  changedBy: Nullable<string>;
  userId: Nullable<number>;
};

/**
 * @POST /proxies/bulk/buy-traffic/price
 * @POST /proxies/bulk/extend-period/price
 * @POST /proxies/{id}/bandwidth-price
 * @POST /proxies/{id}/period-extension-price
 * @POST /proxies/{id}/reactivate/price
 */
export type ProxyExtensionPriceDTO = {
  finalPrice: number;
  priceNoDiscounts: number;
  discount: number;
  unitPrice: number;
  additionalAmount: number;
  additionalAmountAfterDiscount: number;
  paymentFee: number;
  subtotal: number;
  discountAmount: number;
  finalPriceInCurrency: number;
  currency: Nullable<CurrencyDTO>;
};

/**
 * @GET /proxies/{id}/events
 */
export type ProxyEventDTO = {
  createdAt: string;
  message: string;
};

/**
 * @GET /services/proxies/isp
 */
export type ProxyISPDTO = {
  id: string;
  name: string;
};

/**
 * @GET /services/proxies/{id}
 */
export type ProxyOverviewDTO = ProxyDTO & {
  plan: Nullable<PlanID>;
  activatedAt: Nullable<string>;
  canceledAt: Nullable<string>;
  updatedAt: Nullable<string>;
  autoExtendEnabled: boolean;
  autoExtendBandwidthAmount: number;
  autoExtendAt: Nullable<string>;
  routes: ProxyRouteDTO[];
  traffic: ProxyTrafficDTO;
  uptime: ProxyUptimeDTO;
  type: string;
  currentUsage: ProxyLimitSignalDTO[];
};

export type ProxyLimitSignalsListItemDTO = {
  proxyId: number;
  ip: string;
  countryCode: Nullable<string>;
  networkType: NetworkType;
  note: Nullable<string>;
  signal: ProxyLimitSignalDTO;
};

/**
 * @GET /services/proxies/limit-signals
 */
export type ProxyLimitSignalsDTO = {
  signals: ProxyLimitSignalsListItemDTO[];
  // TODO: confirm with backend that this field is actually returned by the endpoint.
  updatedAt: Nullable<string>;
};

/**
 * @GET /services/proxies/replacement/check
 */
export type ProxyReplacementCheckDTO = {
  isReplacementAvailable: boolean;
};

/**
 * @GET /services/proxies/replacement/list
 */
export type ProxyReplacementDetailsDTO = {
  id: number;
  status: ProxyStatusDTO;
  networkType: NetworkType;
  publicIp: string;
  createdAt: string;
  expiresAt: string;
  ispId: string;
  countryCode: string;
  note: Nullable<string>;
  isUnusedProxy: boolean;
  ipVersion: Nullable<IPVersion>;
};

/**
 * @POST /order/configuration
 */
export type ProxyReplacementOptionsDTO = {
  isUnused: boolean;
  supportedCountries: Array<{ code: string; amount: Nullable<string> }>;
  supportedISPs: [] | Record<string, [] | Record<string, { name: string; amount: Nullable<string> }>>;
};

/**
 * @GET /proxies/{id}/admin/routes/details
 */
export type ProxySubnetDTO = {
  id: string;
  cidr: string;
  serverName: string;
  countryCode: string;
};

/**
 * @GET /proxies/summary
 */
export type ProxySummaryDTO = {
  activeProxyCount: number;
  inactiveProxyCount: number;
  expiringProxyCount: number;
};

/**
 * @GET /proxies/{id}/buy-threads
 */
export type UpgradeThreadsPriceDTO = {
  currentThreads: number;
  availableThreads: UpgradeableOption[];
};

/**
 * @GET /proxies/{id}/buy-uplink-speed
 */
export type UpgradeUplinkSpeedPriceDTO = {
  currentUplinkSpeed: number;
  availableUplinkSpeeds: UpgradeableOption[];
};

/**
 * @POST /proxies/bulk/extend-period
 */
export type BulkOperationDTO = {
  operationId: string;
};

/**
 * @GET /bulk-operations/{operationId}
 */
export enum OperationTypeDTO {
  PERIOD_EXTENSION = 'PERIOD_EXTENSION',
  TRAFFIC_PURCHASE = 'TRAFFIC_PURCHASE',
}

export type ProxyBulkOperationDTO = {
  id: string;
  type: OperationTypeDTO;
  changes: ProxyBulkOperationChanges;
  status: OperationStatus;
  total: number;
  processed: number;
  failureReasons: string[];
};

/**
 * @GET /bulk-operations/{operationId}/tasks
 */
export type ProxyBulkOperationTaskDetailsDTO = {
  id: number;
  note: Nullable<string>;
  publicIp: string;
  networkType: NetworkType;
  expiresAt: string;
  protocol: Protocol;
  authType: AuthenticationType;
  isAutoExtendable: boolean;
  trafficLimit: Nullable<number>;
};

export type ProxyBulkOperationTaskDTO = {
  type: 'proxy';
  reason: string;
  details: ProxyBulkOperationTaskDetailsDTO;
};
